import React, { useState, useEffect, useMemo } from "react";
import { 
    Card, Table, Row, Col, Form, InputGroup, Button, Badge, 
    Spinner, Alert, Tooltip, OverlayTrigger, Modal, Pagination 
} from "react-bootstrap";
import { Link, useNavigate } from "react-router-dom";
import Footer from "../layouts/Footer";
import Header from "../layouts/Header";
import { formatCurrency, formatDate } from "../utils/formatters";
import { API_BASE_URL } from "../constants/api";


export default function Reglements() {
    const navigate = useNavigate();
    const [reglements, setReglements] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [filterText, setFilterText] = useState('');
    const [stats, setStats] = useState(null);
    const [filtreStatut, setFiltreStatut] = useState('tous');
    const [filtreMode, setFiltreMode] = useState('tous');
    const [showFilters, setShowFilters] = useState(false);
    const [sortConfig, setSortConfig] = useState({ key: 'date_reglement', direction: 'desc' });

    // États pour la pagination
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(10);

    const [showSuccessAlert, setShowSuccessAlert] = useState(false);
    const [showErrorAlert, setShowErrorAlert] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');
    const [errorMessage, setErrorMessage] = useState('');

    // États pour le modal de confirmation
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [confirmAction, setConfirmAction] = useState(null);
    const [confirmId, setConfirmId] = useState(null);
    const [confirmTitle, setConfirmTitle] = useState('');
    const [confirmMessage, setConfirmMessage] = useState('');
    const [confirmVariant, setConfirmVariant] = useState('primary');
    const [isProcessing, setIsProcessing] = useState(false);

    useEffect(() => {
        fetchReglements();
        fetchStats();
    }, [filtreStatut, filtreMode]);

    const openConfirmModal = (id, title, message, variant = 'primary', action) => {
        setConfirmId(id);
        setConfirmTitle(title);
        setConfirmMessage(message);
        setConfirmVariant(variant);
        setConfirmAction(() => action);
        setShowConfirmModal(true);
    };

    const fetchReglements = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem("token");
            let url = `${API_BASE_URL}/reglements.php`;
            const params = new URLSearchParams();
            
            if (filtreStatut !== 'tous') params.append('statut', filtreStatut);
            if (filtreMode !== 'tous') params.append('mode_reglement', filtreMode);
            
            if (params.toString()) url += '?' + params.toString();
            
            const response = await fetch(url, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            
            if (!response.ok) throw new Error("Erreur chargement");
            
            const result = await response.json();
            setReglements(result.data || []);
            setCurrentPage(1);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const fetchStats = async () => {
        try {
            const token = localStorage.getItem("token");
            const response = await fetch(
                `${API_BASE_URL}/reglements.php?action=stats`,
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            
            if (response.ok) {
                const result = await response.json();
                setStats(result.data);
            }
        } catch (err) {
            console.error("Erreur stats", err);
        }
    };

    const showSuccess = (message) => {
        setSuccessMessage(message);
        setShowSuccessAlert(true);
        setTimeout(() => setShowSuccessAlert(false), 3000);
    };

    const showError = (message) => {
        setErrorMessage(message);
        setShowErrorAlert(true);
        setTimeout(() => setShowErrorAlert(false), 3000);
    };

    const handleValider = async (id) => {
        openConfirmModal(
            id,
            "Valider le règlement",
            "Êtes-vous sûr de vouloir valider ce règlement ? Le solde du client et de la facture sera mis à jour.",
            "success",
            async (confirmId) => {
                setIsProcessing(true);
                try {
                    const token = localStorage.getItem("token");
                    const response = await fetch(
                        `${API_BASE_URL}/reglements.php`,
                        {
                            method: 'POST',
                            headers: { 
                                'Authorization': `Bearer ${token}`,
                                'Content-Type': 'application/json'
                            },
                            body: JSON.stringify({
                                action: 'valider',
                                id: confirmId
                            })
                        }
                    );

                    const result = await response.json();

                    if (result.success) {
                        setShowConfirmModal(false);
                        showSuccess("Règlement validé avec succès");
                        fetchReglements();
                        fetchStats();
                    } else {
                        showError(result.message || "Erreur lors de la validation");
                    }
                } catch (err) {
                    showError("Erreur de connexion au serveur");
                } finally {
                    setIsProcessing(false);
                }
            }
        );
    };

    const handleSupprimer = async (id) => {
        openConfirmModal(
            id,
            "Confirmer la suppression",
            "Êtes-vous sûr de vouloir supprimer ce règlement ? Cette action est irréversible.",
            "danger",
            async (confirmId) => {
                setIsProcessing(true);
                try {
                    const token = localStorage.getItem("token");
                    const response = await fetch(
                        `${API_BASE_URL}/reglements.php?id=${confirmId}`,
                        {
                            method: 'DELETE',
                            headers: { 'Authorization': `Bearer ${token}` }
                        }
                    );

                    const result = await response.json();

                    if (result.success) {
                        setShowConfirmModal(false);
                        showSuccess("Règlement supprimé avec succès");
                        fetchReglements();
                        fetchStats();
                    } else {
                        showError(result.message || "Erreur lors de la suppression");
                    }
                } catch (err) {
                    showError("Erreur de connexion au serveur");
                } finally {
                    setIsProcessing(false);
                }
            }
        );
    };

    const getStatutBadge = (statut) => {
        const configs = {
            en_attente: { color: '#f59e0b', icon: 'ri-time-line', label: 'En attente' },
            valide: { color: '#10b981', icon: 'ri-check-double-line', label: 'Validé' },
            rejete: { color: '#ef4444', icon: 'ri-close-circle-line', label: 'Rejeté' }
        };
        const config = configs[statut] || configs.en_attente;
        return (
            <span className="status-badge" style={{ background: `${config.color}15`, color: config.color }}>
                <i className={`${config.icon} me-1 fs-10`}></i>
                {config.label}
            </span>
        );
    };

    const getModeIcon = (mode) => {
        const icons = {
            especes: 'ri-money-euro-circle-line', 
            cheque: 'ri-bank-card-line',
            virement: 'ri-bank-line',
            carte_bancaire: 'ri-smartphone-line',
            traite: 'ri-file-copy-line'
        };
        return icons[mode] || 'ri-bank-card-line';
    };

    const getModeLabel = (mode) => {
        const labels = {
            especes: 'Espèces',
            cheque: 'Chèque',
            virement: 'Virement',
            carte_bancaire: 'Carte bancaire',
            traite: 'Traite'
        };
        return labels[mode] || mode;
    };

    const filteredReglements = useMemo(() => {
        return reglements
            .filter(r => 
                r.numero_reglement?.toLowerCase().includes(filterText.toLowerCase()) ||
                r.client_nom?.toLowerCase().includes(filterText.toLowerCase()) ||
                r.numero_facture?.toLowerCase().includes(filterText.toLowerCase()) ||
                r.reference?.toLowerCase().includes(filterText.toLowerCase())
            )
            .sort((a, b) => {
                if (sortConfig.key === 'date_reglement') {
                    return sortConfig.direction === 'asc' 
                        ? new Date(a.date_reglement) - new Date(b.date_reglement)
                        : new Date(b.date_reglement) - new Date(a.date_reglement);
                }
                if (sortConfig.key === 'montant') {
                    return sortConfig.direction === 'asc' 
                        ? a.montant - b.montant
                        : b.montant - a.montant;
                }
                return 0;
            });
    }, [reglements, filterText, sortConfig]);

    // Pagination
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentItems = filteredReglements.slice(indexOfFirstItem, indexOfLastItem);
    const totalPages = Math.ceil(filteredReglements.length / itemsPerPage);

    const handlePageChange = (pageNumber) => {
        setCurrentPage(pageNumber);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const renderPagination = () => {
        let items = [];
        const maxVisible = 5;
        let startPage = Math.max(1, currentPage - Math.floor(maxVisible / 2));
        let endPage = Math.min(totalPages, startPage + maxVisible - 1);

        if (endPage - startPage + 1 < maxVisible) {
            startPage = Math.max(1, endPage - maxVisible + 1);
        }

        if (startPage > 1) {
            items.push(
                <Pagination.Item key={1} onClick={() => handlePageChange(1)}>
                    1
                </Pagination.Item>
            );
            if (startPage > 2) {
                items.push(<Pagination.Ellipsis key="ellipsis-start" disabled />);
            }
        }

        for (let number = startPage; number <= endPage; number++) {
            items.push(
                <Pagination.Item 
                    key={number} 
                    active={number === currentPage}
                    onClick={() => handlePageChange(number)}
                >
                    {number}
                </Pagination.Item>
            );
        }

        if (endPage < totalPages) {
            if (endPage < totalPages - 1) {
                items.push(<Pagination.Ellipsis key="ellipsis-end" disabled />);
            }
            items.push(
                <Pagination.Item key={totalPages} onClick={() => handlePageChange(totalPages)}>
                    {totalPages}
                </Pagination.Item>
            );
        }

        return items;
    };

    const handleSort = (key) => {
        setSortConfig(prev => ({
            key,
            direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
        }));
    };

    const SortableHeader = ({ field, children }) => (
        <th 
            onClick={() => handleSort(field)}
            style={{ cursor: 'pointer' }}
            className="sortable-header"
        >
            <div className="d-flex align-items-center gap-1">
                {children}
                {sortConfig.key === field && (
                    <i className={`ri-arrow-${sortConfig.direction === 'asc' ? 'up' : 'down'}-s-line`}></i>
                )}
            </div>
        </th>
    );

    const ActionButton = ({ variant, icon, onClick, tooltip, disabled = false }) => (
        <OverlayTrigger placement="top" overlay={<Tooltip id={`tooltip-${variant}`}>{tooltip}</Tooltip>}>
            <Button variant={variant} size="sm" onClick={onClick} disabled={disabled} className="action-btn me-1">
                <i className={icon}></i>
            </Button>
        </OverlayTrigger>
    );

    if (loading) {
        return (
            <>
                <Header />
                <div className="main main-app p-3 p-lg-4" style={{ background: '#f8fafc', minHeight: '100vh' }}>
                    <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 'calc(100vh - 100px)' }}>
                        <div className="text-center">
                            <div className="spinner-border text-primary mb-3" style={{ width: '3rem', height: '3rem', color: '#21ACCD' }} role="status">
                                <span className="visually-hidden">Chargement...</span>
                            </div>
                            <h5 className="text-muted">Chargement des règlements...</h5>
                        </div>
                    </div>
                </div>
                <Footer />
            </>
        );
    }

    if (error) {
        return (
            <>
                <Header />
                <div className="main main-app p-3 p-lg-4">
                    <Alert variant="danger" className="border-0 shadow-sm rounded-3">
                        <div className="d-flex align-items-center">
                            <i className="ri-error-warning-line fs-1 me-3 text-danger"></i>
                            <div>
                                <h6 className="mb-1 fw-bold">Erreur de chargement</h6>
                                <p className="mb-0 small">{error}</p>
                                <Button variant="outline-danger" size="sm" className="mt-2 rounded-pill" onClick={fetchReglements}>
                                    <i className="ri-refresh-line me-1"></i>Réessayer
                                </Button>
                            </div>
                        </div>
                    </Alert>
                </div>
                <Footer />
            </>
        );
    }

    return (
        <>
            <Header />
            <div className="main main-app p-3 p-lg-4" style={{ background: '#f8fafc', minHeight: '100vh' }}>
                <div className="container-fluid">
                    {/* Alertes */}
                    {showSuccessAlert && (
                        <Alert variant="success" onClose={() => setShowSuccessAlert(false)} dismissible className="mb-4 alert-modern">
                            <i className="ri-checkbox-circle-line me-2 fs-5"></i>
                            {successMessage}
                        </Alert>
                    )}
                    
                    {showErrorAlert && (
                        <Alert variant="danger" onClose={() => setShowErrorAlert(false)} dismissible className="mb-4 alert-modern">
                            <i className="ri-error-warning-line me-2 fs-5"></i>
                            {errorMessage}
                        </Alert>
                    )}

                    {/* En-tête simplifié */}
                    <div className="d-md-flex align-items-center justify-content-between mb-4">
                        <div>
                            <div className="d-flex align-items-center gap-2 mb-2">
                                <div className="p-2 bg-primary bg-opacity-10 rounded-3">
                                    <i className="ri-bank-card-line fs-20 text-primary" style={{ color: '#21ACCD' }}></i>
                                </div>
                                <div>
                                    <h4 className="main-title mb-0 fw-bold">Règlements</h4>
                                    <p className="text-muted small mb-0">Gérez les paiements et encaissements</p>
                                </div>
                            </div>
                        </div>
                        <div className="d-flex gap-2 mt-3 mt-md-0">
                            <Button 
                                variant="primary" 
                                onClick={() => navigate('/ventes/NouveauReglement')}
                                className="rounded-pill px-3 d-flex align-items-center gap-1"
                                size="sm"
                                style={{ background: '#21ACCD', border: 'none' }}
                            >
                                <i className="ri-add-line"></i>
                                Nouveau règlement
                            </Button>
                        </div>
                    </div>

                    {/* KPI Cards */}
                    {stats && (
                        <Row className="g-3 mb-4">
                            <Col sm={6} xl={6}>
                                <div className="kpi-card kpi-primary">
                                    <div className="kpi-icon">
                                        <i className="ri-bank-card-line fs-24"></i>
                                    </div>
                                    <div className="kpi-content">
                                        <span className="kpi-label">Ce mois</span>
                                        <h3 className="kpi-value">{stats.mois?.nb_reglements || 0}</h3>
                                        <span className="kpi-unit">{formatCurrency(stats.mois?.total_montant || 0)}</span>
                                    </div>
                                </div>
                            </Col>
                            <Col sm={6} xl={6}>
                                <div className="kpi-card kpi-danger">
                                    <div className="kpi-icon">
                                        <i className="ri-error-warning-line fs-24"></i>
                                    </div>
                                    <div className="kpi-content">
                                        <span className="kpi-label">Impayés</span>
                                        <h3 className="kpi-value">{stats.impayes?.nb_factures || 0}</h3>
                                        <span className="kpi-unit">{formatCurrency(stats.impayes?.total_impaye || 0)}</span>
                                    </div>
                                </div>
                            </Col>
                        </Row>
                    )}

                    {/* Filtres */}
                    <Card className="border-0 shadow-sm rounded-3 mb-4">
                        <Card.Body className="py-3 px-4">
                            <Row className="align-items-center g-3">
                                <Col md={4}>
                                    <div className="position-relative">
                                        <i className="ri-search-line position-absolute top-50 start-0 translate-middle-y ms-3 text-muted fs-14"></i>
                                        <Form.Control
                                            type="text"
                                            placeholder="Rechercher règlement, client..."
                                            value={filterText}
                                            onChange={(e) => setFilterText(e.target.value)}
                                            className="ps-5 rounded-pill"
                                        />
                                    </div>
                                </Col>
                                <Col md={3}>
                                    <Form.Select 
                                        value={filtreStatut} 
                                        onChange={(e) => setFiltreStatut(e.target.value)}
                                        className="rounded-pill"
                                    >
                                        <option value="tous">Tous les statuts</option>
                                        <option value="en_attente"> En attente</option>
                                        <option value="valide"> Validés</option>
                                        <option value="rejete"> Rejetés</option>
                                    </Form.Select>
                                </Col>
                                <Col md={3}>
                                    <Form.Select 
                                        value={filtreMode} 
                                        onChange={(e) => setFiltreMode(e.target.value)}
                                        className="rounded-pill"
                                    >
                                        <option value="tous">Tous les modes</option>
                                        <option value="especes">Espèces</option>
                                        <option value="cheque">Chèque</option>
                                        <option value="virement">Virement</option>
                                        <option value="carte_bancaire">Carte bancaire</option>
                                        <option value="traite">Traite</option>
                                    </Form.Select>
                                </Col>
                                <Col md={2} className="text-end">
                                    <span className="badge bg-light text-dark rounded-pill px-3 py-2">
                                        <i className="ri-stack-line me-1"></i>
                                        {filteredReglements.length} règlement(s)
                                    </span>
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>

                    {/* Liste des règlements */}
                    <Card className="border-0 shadow-sm rounded-3 overflow-hidden">
                        <Card.Body className="p-0">
                            <div className="table-responsive">
                                <Table hover className="align-middle mb-0">
                                    <thead className="bg-light">
                                        <tr>
                                            <th className="ps-4">N° Règlement</th>
                                            <SortableHeader field="date_reglement">Date</SortableHeader>
                                            <th>Client</th>
                                            <th>Facture</th>
                                            <th>Mode</th>
                                            <SortableHeader field="montant">Montant</SortableHeader>
                                            <th>Statut</th>
                                            <th className="text-center pe-4">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {currentItems.length > 0 ? (
                                            currentItems.map((reg) => (
                                                <tr key={reg.id_reglement} className="payment-row">
                                                    <td className="ps-4">
                                                        <Link to={`/ventes/reglements/${reg.id_reglement}`} className="table-link">
                                                            {reg.numero_reglement}
                                                        </Link>
                                                    </td>
                                                    <td>{formatDate(reg.date_reglement)}</td>
                                                    <td>
                                                        <div className="fw-semibold">{reg.client_nom}</div>
                                                        <small className="text-muted">{reg.code_client}</small>
                                                    </td>
                                                    <td>
                                                        {reg.numero_facture ? (
                                                            <Link to={`/ventes/facturesDetails/${reg.id_facture}`} className="table-link">
                                                                {reg.numero_facture}
                                                            </Link>
                                                        ) : <span className="text-muted">-</span>}
                                                    </td>
                                                    <td>
                                                        <div className="d-flex align-items-center gap-2">
                                                            <div className="mode-icon">
                                                                <i className={`${getModeIcon(reg.mode_reglement)} text-primary`}></i>
                                                            </div>
                                                            <div>
                                                                <span className="fw-semibold">{getModeLabel(reg.mode_reglement)}</span>
                                                                {reg.reference && <div><small className="text-muted">{reg.reference}</small></div>}
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td className="fw-bold text-primary">{formatCurrency(reg.montant)}</td>
                                                    <td>{getStatutBadge(reg.statut)}</td>
                                                    <td className="text-center pe-4">
                                                        <div className="d-flex gap-2 justify-content-center">
                                                            <ActionButton
                                                                variant="outline-info"
                                                                icon="ri-eye-line"
                                                                onClick={() => navigate(`/ventes/reglements/${reg.id_reglement}`)}
                                                                tooltip="Voir détails"
                                                            />            
                                                            {reg.statut === 'en_attente' && (
                                                                <ActionButton
                                                                    variant="outline-success"
                                                                    icon="ri-check-double-line"
                                                                    onClick={() => handleValider(reg.id_reglement)}
                                                                    tooltip="Valider"
                                                                />
                                                            )}  
                                                            <ActionButton
                                                                variant="outline-danger"
                                                                icon="ri-delete-bin-line"
                                                                onClick={() => handleSupprimer(reg.id_reglement)}
                                                                tooltip="Supprimer"
                                                            />
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))
                                        ) : (
                                            <tr>
                                                <td colSpan="8" className="text-center py-5">
                                                    <div className="empty-state">
                                                        <i className="ri-bank-card-line empty-icon text-primary" style={{ fontSize: '4rem' }}></i>
                                                        <h5 className="mt-3">Aucun règlement trouvé</h5>
                                                        <p className="text-muted mb-3">Commencez par saisir un nouveau règlement</p>
                                                        <Button 
                                                            variant="primary" 
                                                            onClick={() => navigate('/ventes/NouveauReglement')}
                                                            className="rounded-pill"
                                                            style={{ background: '#21ACCD', border: 'none' }}
                                                        >
                                                            <i className="ri-add-line me-2"></i>Nouveau règlement
                                                        </Button>
                                                    </div>
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </Table>
                            </div>

                            {/* Pagination */}
                            {totalPages > 1 && (
                                <div className="d-flex justify-content-between align-items-center p-3 border-top">
                                    <div className="text-muted small">
                                        <i className="ri-file-list-line me-1"></i>
                                        Affichage de {indexOfFirstItem + 1} à {Math.min(indexOfLastItem, filteredReglements.length)} sur {filteredReglements.length} règlements
                                    </div>
                                    <Pagination className="mb-0">
                                        <Pagination.Prev onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1} />
                                        {renderPagination()}
                                        <Pagination.Next onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages} />
                                    </Pagination>
                                </div>
                            )}
                        </Card.Body>
                    </Card>
                </div>
                <Footer />
            </div>

            {/* Modal de confirmation moderne */}
            <Modal show={showConfirmModal} onHide={() => setShowConfirmModal(false)} centered className="modern-modal">
                <Modal.Header closeButton className="border-0 pb-0 bg-info" style={{background:'#F8F8F8'}}>
                    <Modal.Title className="w-100">
                        <div className="text-center">
                            <div className="confirm-icon mb-3">
                                {confirmVariant === 'success' && (
                                    <i className="ri-check-double-line text-success" style={{ fontSize: '4rem' }}></i>
                                )}
                                {confirmVariant === 'danger' && (
                                    <i className="ri-delete-bin-line text-danger" style={{ fontSize: '4rem' }}></i>
                                )}
                                {confirmVariant === 'primary' && (
                                    <i className="ri-information-line text-primary" style={{ fontSize: '4rem' }}></i>
                                )}
                            </div>
                            <h3 className="fw-bold">{confirmTitle}</h3>
                        </div>
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body className="pt-2 text-center">
                    <p className="text-muted mb-4" dangerouslySetInnerHTML={{ __html: confirmMessage }}></p>
                    {confirmVariant === 'danger' && (
                        <Alert variant="warning" className="rounded-3">
                            <i className="ri-alert-line me-2"></i>
                            Cette action est irréversible.
                        </Alert>
                    )}
                </Modal.Body>
                <Modal.Footer className="border-0 pt-0 justify-content-center">
                    <Button variant="outline-secondary" onClick={() => setShowConfirmModal(false)} className="px-4 rounded-pill">
                        Annuler
                    </Button>
                    <Button variant={confirmVariant} onClick={() => confirmAction(confirmId)} disabled={isProcessing} className="px-5 rounded-pill">
                        {isProcessing ? <Spinner size="sm" className="me-2" /> : null}
                        Confirmer
                    </Button>
                </Modal.Footer>
            </Modal>

            <style>{`
                /* KPI Cards */
                .kpi-card {
                    background: white;
                    border-radius: 0px;
                    padding: 1rem;
                    display: flex;
                    align-items: center;
                    gap: 1rem;
                    box-shadow: 0 1px 3px rgba(0,0,0,0.05);
                    transition: all 0.2s ease;
                    height: 100%;
                    border-left: 2px solid;
                }
                .kpi-card:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 8px 20px rgba(0,0,0,0.08);
                }
                .kpi-primary { border-left-color: #21ACCD; }
                .kpi-danger { border-left-color: #ef4444; }
                
                .kpi-icon {
                    width: 48px;
                    height: 48px;
                    border-radius: 12px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                }
                .kpi-primary .kpi-icon { background: #e2f3f5; color: #21ACCD; }
                .kpi-danger .kpi-icon { background: #fef2f2; color: #ef4444; }
                
                .kpi-content {
                    flex: 1;
                }
                .kpi-label {
                    font-size: 0.7rem;
                    text-transform: uppercase;
                    color: #6c757d;
                    font-weight: 600;
                    letter-spacing: 0.5px;
                }
                .kpi-value {
                    font-size: 1.3rem;
                    font-weight: 700;
                    margin: 0.25rem 0;
                    color: #1a3c5e;
                }
                .kpi-unit {
                    font-size: 0.7rem;
                    color: #6c757d;
                }
                
                /* Status Badge */
                .status-badge {
                    display: inline-flex;
                    align-items: center;
                    padding: 0.3rem 0.8rem;
                    border-radius: 30px;
                    font-size: 0.7rem;
                    font-weight: 500;
                }
                
                /* Action Buttons */
                .action-btn {
                    width: 32px;
                    height: 32px;
                    border-radius: 8px;
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    transition: all 0.2s ease;
                    border: 1px solid #e2e8f0;
                    background: white;
                    color: #64748b;
                }
                .action-btn:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 4px 8px rgba(0,0,0,0.1);
                }
                .btn-outline-info:hover {
                    background: linear-gradient(135deg, #0891b2, #0e7490);
                    border-color: #0891b2;
                    color: white;
                }
                .btn-outline-success:hover {
                    background: linear-gradient(135deg, #10b981, #059669);
                    border-color: #10b981;
                    color: white;
                }
                .btn-outline-danger:hover {
                    background: linear-gradient(135deg, #ef4444, #dc2626);
                    border-color: #ef4444;
                    color: white;
                }
                
                /* Table Link */
                .table-link {
                    color: #21ACCD;
                    text-decoration: none;
                    font-weight: 600;
                }
                .table-link:hover {
                    text-decoration: underline;
                }
                
                .mode-icon {
                    width: 32px;
                    height: 32px;
                    background: #e2f3f5;
                    border-radius: 8px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                }
                
                .payment-row {
                    transition: all 0.2s ease;
                }
                
                .payment-row:hover {
                    background: #f8fafc;
                }
                
                /* Pagination */
                .pagination {
                    gap: 5px;
                }
                .pagination .page-item .page-link {
                    border-radius: 8px;
                    border: 1px solid #e2e8f0;
                    color: #1e293b;
                    padding: 0.5rem 1rem;
                    transition: all 0.2s ease;
                    font-weight: 500;
                }
                .pagination .page-item.active .page-link {
                    background: linear-gradient(135deg, #21ACCD, #1a8ba8);
                    border-color: #21ACCD;
                    color: white;
                    box-shadow: 0 4px 10px rgba(33, 172, 205, 0.3);
                }
                .pagination .page-item .page-link:hover {
                    background: #f1f5f9;
                    transform: translateY(-2px);
                    box-shadow: 0 4px 8px rgba(0,0,0,0.1);
                }
                .pagination .page-item.disabled .page-link {
                    opacity: 0.5;
                    pointer-events: none;
                }
                
                .modern-modal .modal-content {
                    border-radius: 20px;
                    border: none;
                    box-shadow: 0 20px 40px rgba(0,0,0,0.2);
                }
                
                .confirm-icon {
                    animation: scaleIn 0.5s ease-out;
                }
                
                @keyframes scaleIn {
                    0% { transform: scale(0); opacity: 0; }
                    50% { transform: scale(1.2); }
                    100% { transform: scale(1); opacity: 1; }
                }
                
                .alert-modern {
                    border: none;
                    border-radius: 12px;
                    padding: 1rem 1.5rem;
                    box-shadow: 0 4px 8px rgba(0,0,0,0.05);
                }
                
                .rounded-3 {
                    border-radius: 12px !important;
                }
                
                .rounded-pill {
                    border-radius: 50px !important;
                }
                
                .fs-10 {
                    font-size: 0.625rem;
                }
                .fs-14 {
                    font-size: 0.875rem;
                }
                .fs-20 {
                    font-size: 1.25rem;
                }
                .fs-24 {
                    font-size: 1.5rem;
                }
            `}</style>
        </>
    );
}